import { jwtDecode } from 'jwt-decode';

export interface AccessMatrix {
  [module: string]: {
    [resource: string]: string[]; // e.g., "performspark.goal_management.goal_cycles": ["create", "delete", "edit"]
  };
}

export interface JWTPayload {
  // Standard JWT claims
  exp?: number; // Expiration time (seconds since epoch)
  iat?: number; // Issued at time
  sub?: string; // Subject (user identifier)

  // Custom claims from Keycloak (only user_id and account_id are synced)
  user_uuid?: string;
  user_id?: string; // Keycloak uses user_id
  email?: string;
  first_name?: string;
  last_name?: string;
  given_name?: string; // Keycloak uses given_name
  family_name?: string; // Keycloak uses family_name
  account_uuid?: string;
  account_id?: string; // Keycloak uses account_id

  // NOTE: RBAC flags are NOT in JWT - they come from /me API
  // is_superadmin, is_hrmanager, is_rc_* flags are fetched from /me endpoint

  // Add any other custom claims your backend includes
  [key: string]: any;
}

/**
 * Decode a JWT token and return its payload
 * @param token - The JWT token string
 * @returns Decoded token payload or null if invalid
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    console.log('🔓 Decoded JWT payload:', decoded);
    return decoded;
  } catch (error) {
    console.error('❌ Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string
 * @param bufferSeconds - Optional buffer time in seconds before actual expiration (default: 60)
 * @returns true if token is expired or will expire within buffer time
 */
export const isTokenExpired = (token: string | null, bufferSeconds: number = 60): boolean => {
  if (!token) {
    return true;
  }

  try {
    const decoded = jwtDecode<JWTPayload>(token);

    if (!decoded.exp) {
      console.warn('⚠️ JWT token has no expiration claim');
      return false; // No exp claim, assume it doesn't expire
    }

    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = bufferSeconds * 1000; // Convert buffer to milliseconds

    const isExpired = currentTime >= (expirationTime - bufferTime);

    if (isExpired) {
      const timeUntilExpiry = Math.floor((expirationTime - currentTime) / 1000);
      console.log(`⏰ Token ${timeUntilExpiry > 0 ? 'will expire in' : 'expired'} ${Math.abs(timeUntilExpiry)}s`);
    }

    return isExpired;
  } catch (error) {
    console.error('❌ Error checking token expiration:', error);
    return true; // Assume expired if we can't decode
  }
};

/**
 * Get the expiration time of a JWT token
 * @param token - The JWT token string
 * @returns Expiration time as Date object or null if invalid
 */
export const getTokenExpiration = (token: string | null): Date | null => {
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode<JWTPayload>(token);

    if (!decoded.exp) {
      return null;
    }

    return new Date(decoded.exp * 1000);
  } catch (error) {
    console.error('❌ Error getting token expiration:', error);
    return null;
  }
};

const normalizeBooleanClaim = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

/**
 * Extract user claims from JWT token
 * NOTE: RBAC flags (is_superadmin, is_hrmanager, is_rc_*) are NOT in JWT
 * They are fetched from the /me API endpoint instead
 * @param token - The JWT token string
 * @returns Object containing user-specific claims (identity only, no RBAC)
 */
export const extractUserClaims = (token: string | null) => {
  if (!token) {
    return null;
  }

  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  console.log('🔓 Raw JWT payload:', payload);

  // Extract only identity claims from JWT
  // RBAC flags come from /me API, not from JWT
  const claims = {
    user_uuid: payload.user_id || payload.user_uuid, // Keycloak uses user_id
    email: payload.email,
    first_name: payload.given_name || payload.first_name, // Keycloak uses given_name
    last_name: payload.family_name || payload.last_name, // Keycloak uses family_name
    account_uuid: payload.account_id || payload.account_uuid, // Keycloak uses account_id
    sub: payload.sub,
    // Customer employee specific claims
    user_category: payload.user_category || 'employee', // 'employee' | 'customer_employee' | 'policyholder'
    is_customer_admin: normalizeBooleanClaim(payload.is_customer_admin),
    access: Array.isArray(payload.access) ? payload.access : [], // Module access: ['LMS', 'TMS', 'Coaching']
  };

  console.log('✅ Extracted JWT identity claims:', claims);
  console.log('ℹ️ Note: RBAC flags are fetched from /me API, not from JWT');

  return claims;
};

/**
 * Get time until token expiration in seconds
 * @param token - The JWT token string
 * @returns Seconds until expiration, or null if invalid/no expiration
 */
export const getTimeUntilExpiration = (token: string | null): number | null => {
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode<JWTPayload>(token);

    if (!decoded.exp) {
      return null;
    }

    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const secondsRemaining = Math.floor((expirationTime - currentTime) / 1000);

    return secondsRemaining;
  } catch (error) {
    return null;
  }
};
